import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ArrowLeftIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { NotificationSettings } from '../types';
import { notificationSettingsApi } from '../services/api';
import { useAppSelector } from '../store/hooks';
import Card from '../components/Card';
import Button from '../components/Button';

interface NotificationFormData {
  emailEnabled: boolean;
  notifyOnKeyAssignment: boolean;
  notifyOnKeyReturn: boolean;
  notifyOnLowInventory: boolean;
  notifyOnNewAccessRequest: boolean;
  lowInventoryThreshold: number;
  graphSenderEmail: string;
}

export default function NotificationSettingsPage() {
  const currentUser = useAppSelector((state) => state.users.currentUser);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [newRecipient, setNewRecipient] = useState('');
  const [graphConfigured, setGraphConfigured] = useState(false);

  const { register, handleSubmit, watch, reset } = useForm<NotificationFormData>({
    defaultValues: {
      emailEnabled: false,
      notifyOnKeyAssignment: true,
      notifyOnKeyReturn: true,
      notifyOnLowInventory: true,
      notifyOnNewAccessRequest: true,
      lowInventoryThreshold: 5,
      graphSenderEmail: '',
    },
  });

  const emailEnabled = watch('emailEnabled');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const [settings, graphStatus] = await Promise.all([
        notificationSettingsApi.get(),
        notificationSettingsApi.getGraphStatus(),
      ]);
      
      setGraphConfigured(graphStatus.allConfigured);
      
      reset({
        emailEnabled: settings.emailEnabled ?? false,
        notifyOnKeyAssignment: settings.notifyOnKeyAssignment ?? true,
        notifyOnKeyReturn: settings.notifyOnKeyReturn ?? true,
        notifyOnLowInventory: settings.notifyOnLowInventory ?? true,
        notifyOnNewAccessRequest: settings.notifyOnNewAccessRequest ?? true,
        lowInventoryThreshold: settings.lowInventoryThreshold ?? 5,
        graphSenderEmail: settings.graphSenderEmail ?? '',
      });
      setEmailRecipients(settings.emailRecipients ?? []);
    } catch (error) {
      console.error('Failed to load settings:', error);
      setMessage({ type: 'error', text: 'Failed to load notification settings' });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: NotificationFormData) => {
    setSaving(true);
    setMessage(null);
    try {
      const updates: Partial<NotificationSettings> = {
        ...data,
        emailRecipients,
        updatedBy: currentUser?.id || 'unknown',
      };

      await notificationSettingsApi.update(updates);
      setMessage({ type: 'success', text: 'Notification settings saved successfully' });
    } catch (error) {
      console.error('Failed to save settings:', error);
      setMessage({ type: 'error', text: 'Failed to save notification settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setMessage({ type: 'error', text: 'Please enter an email address to test' });
      return;
    }
    setTesting(true);
    setMessage(null);
    try {
      const result = await notificationSettingsApi.testEmail(testEmail);
      setMessage({ type: 'success', text: result.message });
    } catch (error) {
      console.error('Test email failed:', error);
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to send test email' });
    } finally {
      setTesting(false);
    }
  };

  const addRecipient = () => {
    if (newRecipient && !emailRecipients.includes(newRecipient)) {
      setEmailRecipients([...emailRecipients, newRecipient]);
      setNewRecipient('');
    }
  };

  const removeRecipient = (email: string) => {
    setEmailRecipients(emailRecipients.filter(e => e !== email));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-3 text-secondary-600 dark:text-secondary-400">Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link
          to="/settings"
          className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Notification Settings</h1>
          <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Configure email notifications and alert thresholds
          </p>
        </div>
      </div>

      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200' 
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Email Toggle */}
        <Card title="Email Notifications">
          <div className="space-y-4">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                {...register('emailEnabled')}
                className="w-4 h-4 rounded border-secondary-300 dark:border-secondary-600 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-secondary-900 dark:text-secondary-100">
                Enable email notifications
              </span>
            </label>

            {emailEnabled && (
              <>
                <div className="border-t border-secondary-200 dark:border-secondary-700 pt-4 mt-4">
                  <h4 className="text-sm font-medium text-secondary-900 dark:text-secondary-100 mb-3">
                    Notification Recipients
                  </h4>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="email"
                      value={newRecipient}
                      onChange={(e) => setNewRecipient(e.target.value)}
                      placeholder="admin@example.com"
                      className="flex-1 px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 placeholder-secondary-400"
                    />
                    <Button type="button" onClick={addRecipient} size="sm">
                      <PlusIcon className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {emailRecipients.map((email) => (
                      <span
                        key={email}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-secondary-100 dark:bg-secondary-700 rounded-full text-sm text-secondary-700 dark:text-secondary-300"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => removeRecipient(email)}
                          className="p-0.5 hover:bg-secondary-200 dark:hover:bg-secondary-600 rounded-full"
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    {emailRecipients.length === 0 && (
                      <span className="text-sm text-secondary-500 dark:text-secondary-400">
                        No recipients added
                      </span>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </Card>

        {/* Notification Triggers */}
        <Card title="Notification Triggers">
          <div className="space-y-3">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                {...register('notifyOnKeyAssignment')}
                className="w-4 h-4 rounded border-secondary-300 dark:border-secondary-600 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-secondary-700 dark:text-secondary-300">
                When a key is assigned to someone
              </span>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                {...register('notifyOnKeyReturn')}
                className="w-4 h-4 rounded border-secondary-300 dark:border-secondary-600 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-secondary-700 dark:text-secondary-300">
                When a key is returned
              </span>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                {...register('notifyOnLowInventory')}
                className="w-4 h-4 rounded border-secondary-300 dark:border-secondary-600 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-secondary-700 dark:text-secondary-300">
                When key inventory falls below threshold
              </span>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                {...register('notifyOnNewAccessRequest')}
                className="w-4 h-4 rounded border-secondary-300 dark:border-secondary-600 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-secondary-700 dark:text-secondary-300">
                When a new user requests access
              </span>
            </label>
          </div>
        </Card>

        {/* Low Inventory Threshold */}
        <Card title="Low Inventory Alert">
          <div className="space-y-4">
            <p className="text-sm text-secondary-600 dark:text-secondary-400">
              Show alerts when available keys fall below this threshold
            </p>
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                Threshold:
              </label>
              <input
                type="number"
                min="1"
                max="100"
                {...register('lowInventoryThreshold', { valueAsNumber: true })}
                className="w-24 px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100"
              />
              <span className="text-sm text-secondary-500 dark:text-secondary-400">keys</span>
            </div>
          </div>
        </Card>

        {/* Microsoft Graph / Office 365 Settings */}
        {emailEnabled && (
          <Card title="Microsoft 365 Email Configuration">
            {!graphConfigured ? (
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Azure AD not fully configured.</strong> Email notifications require the following environment variables to be set in Azure:
                </p>
                <ul className="mt-2 text-sm text-yellow-700 dark:text-yellow-300 list-disc list-inside">
                  <li>AAD_TENANT_ID</li>
                  <li>AAD_CLIENT_ID</li>
                  <li>AAD_CLIENT_SECRET</li>
                </ul>
                <p className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                  The Azure AD app also needs the <strong>Mail.Send</strong> application permission in Microsoft Graph.
                </p>
              </div>
            ) : (
              <>
                <div className="p-4 mb-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-sm text-green-800 dark:text-green-200">
                    ✓ Azure AD credentials are configured. Emails will be sent via Microsoft Graph API.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Sender Email Address *
                    </label>
                    <input
                      type="email"
                      {...register('graphSenderEmail')}
                      placeholder="notifications@yourcompany.com"
                      className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 placeholder-secondary-400"
                    />
                    <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                      This must be a valid mailbox in your Microsoft 365 tenant (e.g., a shared mailbox or user account).
                      The Azure AD app must have permission to send as this user.
                    </p>
                  </div>

                  {/* Test Email */}
                  <div className="pt-4 border-t border-secondary-200 dark:border-secondary-700">
                    <h4 className="text-sm font-medium text-secondary-900 dark:text-secondary-100 mb-3">
                      Test Email Configuration
                    </h4>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="test@example.com"
                        className="flex-1 px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 placeholder-secondary-400"
                      />
                      <Button type="button" variant="secondary" onClick={handleTestEmail} disabled={testing}>
                        {testing ? 'Sending...' : 'Send Test'}
                      </Button>
                    </div>
                    <p className="mt-2 text-xs text-secondary-500 dark:text-secondary-400">
                      Save your settings first, then send a test email to verify the configuration.
                    </p>
                  </div>
                </div>
              </>
            )}
          </Card>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </form>
    </div>
  );
}
